import { cityKey, NO_COUNTRY } from '../../data/pre-event-city-key';
import type { CatalogueRecord } from './match';
import { parseWallClock, wallOrder } from './wall-clock';

export type ApiTerm = {
  slug: string;
  label: string;
};

export type ApiPreEvent = {
  id: number;
  name: string;
  startDate: string | null;
  endDate: string | null;
  timezone: string;
  organisationName: string | null;
  place: string | null;
  city: string | null;
  countryCode: string;
  attendance: ApiTerm[];
  access: ApiTerm | null;
  price: ApiTerm | null;
  description: string | null;
  coverUrl: string;
  coverAlt: string | null;
  ctaUrl: string;
  ctaLabel: string;
  contactEmail: string;
  contactLabel: string;
  theme: ApiTerm | null;
  types: ApiTerm[];
  language: string | null;
};

export type IndexedPreEvent = {
  event: ApiPreEvent;
  record: CatalogueRecord;
};

export const parseCatalogue = (payload: unknown): IndexedPreEvent[] => {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.flatMap((item) => {
    const event = parseEvent(item);
    if (event == null) {
      return [];
    }

    const indexed = indexEvent(event);
    return indexed == null ? [] : [indexed];
  });
};

const indexEvent = (event: ApiPreEvent): IndexedPreEvent | undefined => {
  const start = parseWallClock(event.startDate);
  if (start == null) {
    return undefined;
  }

  const end = parseWallClock(event.endDate);
  const city = event.city ?? '';
  const country = event.countryCode === '' ? NO_COUNTRY : event.countryCode;

  return {
    event,
    record: {
      wallStart: wallOrder(start),
      endAt: end?.instant ?? start.instant,
      wallDay: start.day,
      month: start.month,
      theme: event.theme?.slug ?? '',
      type: event.types.map((type) => type.slug),
      attendance: event.attendance.map((item) => item.slug),
      access: event.access?.slug ?? '',
      language: event.language == null || event.language === '' ? [] : [event.language],
      cityKeys: city === '' ? [] : [cityKey(country, city)],
    },
  };
};

const parseEvent = (value: unknown): ApiPreEvent | undefined => {
  if (!isRecord(value) || typeof value.id !== 'number' || typeof value.name !== 'string' || value.name === '') {
    return undefined;
  }

  const cta = linkPair(value.cta, 'url');
  const contact = linkPair(value.contact, 'email');
  const cover = parseCover(value.cover, value.name);

  return {
    id: value.id,
    name: value.name,
    startDate: nullableString(value.startDate),
    endDate: nullableString(value.endDate),
    timezone: typeof value.timezone === 'string' ? value.timezone : '',
    organisationName: nullableString(value.organisationName),
    place: nullableString(value.place),
    city: nullableString(value.city),
    countryCode: parseCountryCode(value.country),
    attendance: parseTerms(value.attendance),
    access: parseTerm(value.access),
    price: parseTerm(value.price),
    description: nullableString(value.description),
    coverUrl: cover.url,
    coverAlt: cover.alt,
    ctaUrl: cta.destination,
    ctaLabel: cta.label,
    contactEmail: contact.destination,
    contactLabel: contact.label,
    theme: parseTerm(value.theme),
    types: parseTerms(value.types),
    language: nullableString(value.language),
  };
};

const parseCover = (
  value: unknown,
  name: string,
): { url: string; alt: string | null } => {
  if (!isRecord(value) || !isRecord(value.urls) || typeof value.urls.large !== 'string' || value.urls.large === '') {
    return { url: '', alt: null };
  }

  const alt = typeof value.alt === 'string' && value.alt !== '' ? value.alt : name;
  return { url: value.urls.large, alt };
};

const linkPair = (
  value: unknown,
  destinationKey: 'url' | 'email',
): { destination: string; label: string } => {
  if (!isRecord(value)) {
    return { destination: '', label: '' };
  }

  const destination = typeof value[destinationKey] === 'string' ? value[destinationKey] : '';
  const label = typeof value.label === 'string' ? value.label : '';
  if (destination === '' || label === '') {
    return { destination: '', label: '' };
  }

  return { destination, label };
};

const parseCountryCode = (value: unknown): string => {
  if (!isRecord(value) || typeof value.code !== 'string') {
    return '';
  }

  return value.code;
};

const parseTerms = (value: unknown): ApiTerm[] =>
  Array.isArray(value) ? value.flatMap((item) => {
    const term = parseTerm(item);
    return term == null ? [] : [term];
  }) : [];

const parseTerm = (value: unknown): ApiTerm | null => {
  if (!isRecord(value) || typeof value.slug !== 'string' || value.slug === '') {
    return null;
  }

  const label = typeof value.label === 'string' && value.label !== '' ? value.label : value.slug;
  return { slug: value.slug, label };
};

const nullableString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);
